import type { JsonValue } from '@carcheck/shared';
import axios from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEAL_STAGES_KEY } from './configForms';
import {
  patchConfigValue,
  useAdminConfigCache,
  type PatchConfigVariables,
} from '../../../api/admin-config';
import { publishToast } from '../../../components/feedback';

/**
 * Orchestrates a single-key config write for the runtime-config table
 * (AUT-131): fires the `PATCH /admin/config/:key`, a success toast,
 * invalidates the `['admin','config']` cache, and maps a backend 400 onto
 * an INLINE field error the row renders. The PATCH is role + permission
 * (`manage_settings`) + audit gated server-side (AUT-138 removed per-action
 * TOTP step-up).
 *
 * 🟠 #4 The 400 `validation_failed` (per-key range guard) is NOT
 *      auto-toasted by the shared interceptor — we read
 *      `details.issues[0].message` and return it as `fieldError` so the row
 *      surfaces it inline rather than as a generic toast.
 * 🟠 #1 `value` is `JsonValue` (the json editor parses before calling).
 *
 * `run` resolves `{ ok: true }` on success, `{ ok: false, fieldError }` on
 * a 400 (caller pins it under the editor), or `{ ok: false }` on a generic
 * failure (generic toast).
 */

/** The backend 400 envelope for a per-key range violation. */
const VALIDATION_CODE = 'validation_failed';

interface ValidationBody {
  code?: string;
  details?: { issues?: { path?: string; message?: string }[] };
}

export interface SetConfigOutcome {
  ok: boolean;
  /** Inline field error mapped from a 400 (caller renders under the editor). */
  fieldError?: string;
}

export interface UseSetConfigResult {
  /** True while the mutation is in flight. */
  busy: boolean;
  /** Run a config write; resolves the outcome (success / inline 400 / generic). */
  run: (variables: PatchConfigVariables) => Promise<SetConfigOutcome>;
}

export const useSetConfig = (): UseSetConfigResult => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const { invalidate } = useAdminConfigCache();

  const run = async (variables: PatchConfigVariables): Promise<SetConfigOutcome> => {
    setBusy(true);
    try {
      await patchConfigValue(variables);
      await invalidate();
      publishToast({
        severity: 'success',
        durationMs: 4000,
        message: t('config.editor.successToast'),
      });
      return { ok: true };
    } catch (err: unknown) {
      // 🟠 #4 — a 400 `validation_failed` is surfaced INLINE (not toasted).
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        // AUT-633 — a refused `deals.stages` write may mean the stored list
        // moved on (RULINGS 16): re-read it, so «Отмена» → «изменить» opens
        // the current one. Awaited, so the outcome follows the fresh list.
        if (variables.key === DEAL_STAGES_KEY) await invalidate();
        const body = err.response.data as ValidationBody | undefined;
        if (body?.code === VALIDATION_CODE) {
          const message = body.details?.issues?.[0]?.message ?? t('config.editor.invalidValue');
          return { ok: false, fieldError: message };
        }
      }
      publishToast({
        severity: 'error',
        durationMs: 6000,
        message: t('config.editor.errorToast'),
      });
      return { ok: false };
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
};

/**
 * Parse the JSON editor textarea into a `JsonValue` (architect guardrail
 * #1 — typed `JsonValue`, never `any`). Returns `{ ok, value }` or
 * `{ ok:false }` on a syntax error so the row blocks the PATCH + renders
 * "Невалидный JSON" inline. We do NOT structurally validate array shape
 * (the backend accepts untyped values) — parse-validity only.
 */
export const parseJsonValue = (text: string): { ok: true; value: JsonValue } | { ok: false } => {
  try {
    return { ok: true, value: JSON.parse(text) as JsonValue };
  } catch {
    return { ok: false };
  }
};
